/**
 * ClubSettingsGeneralPage - Club Details, Avatar, and External Links
 * Issue #901: Split from monolithic ClubSettingsPage
 *
 * Sections:
 * - Club Details (name, description, location)
 * - Club Avatar (upload, remove)
 * - External Links (website, social)
 */

import { useState, useEffect } from 'react';
import { guardedFetch } from '../../lib/accountGuard';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useClub, useUpdateClub } from '../../hooks/useClubs';
import { TopBar, Button, Input, Skeleton, ConfirmDialog, OroLoader } from '../../components';
import { type ExternalLink, type ExternalLinkType } from '../../lib/api/clubs';
import { emptyToNull } from '../../lib/api/clubFields';
import { API_BASE_URL } from '../../lib/api';
import { Camera, MapPin, Globe, Link as LinkIcon, Plus, X, Youtube, Check, Zap, Instagram, Facebook, Clock } from 'lucide-react';
import type { MeetingMode, ClubRegion } from '../../lib/api/clubs';
import { US_STATES, type USState } from '../../constants/usStates';
import {
  STATE_TO_TIMEZONE,
  COMMON_TIMEZONES,
  timezoneAbbreviation,
} from '../../constants/usStateTimezones';

// External links configuration (#758, #1109, #1184 removed strava)
const LINK_TYPES: { type: ExternalLinkType; label: string; icon: React.ReactNode; placeholder: string }[] = [
  { type: 'website', label: 'Website', icon: <Globe className="w-4 h-4" />, placeholder: 'yourclub.com' },
  { type: 'instagram', label: 'Instagram', icon: <Instagram className="w-4 h-4 text-brand-instagram" />, placeholder: 'instagram.com/yourclub' },
  { type: 'facebook', label: 'Facebook', icon: <Facebook className="w-4 h-4 text-brand-facebook" />, placeholder: 'facebook.com/groups/yourclub' },
  { type: 'youtube', label: 'YouTube', icon: <Youtube className="w-4 h-4 text-brand-youtube" />, placeholder: 'youtube.com/@yourchannel' },
  { type: 'custom', label: 'Custom Link', icon: <LinkIcon className="w-4 h-4" />, placeholder: 'example.com/page' },
];

export default function ClubSettingsGeneralPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Data fetching
  const { data: clubData, isLoading, error } = useClub(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isOwner = membership?.is_owner || false;

  // Mutation
  const updateClubMutation = useUpdateClub(slug || '');

  // Club details form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editMeetingMode, setEditMeetingMode] = useState<MeetingMode>('in_person');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editRegion, setEditRegion] = useState<ClubRegion | null>(null);
  // #1656: club timezone — drives notification rendering for in-person events
  const [editTimezone, setEditTimezone] = useState<string>('America/New_York');
  // Tracks whether the admin has manually picked a tz DURING THIS EDIT
  // SESSION. Initialized to false on every page load — even for clubs with
  // a persisted timezone — because clubs.timezone always has a value
  // (migration 0020 column default + create handler binds 'America/New_York'
  // fallback). Confusing this flag with "row has any value" was Frank P1
  // on PR #1657 — the owner-travel mitigation depends on auto-derive firing
  // when the admin changes state on an existing club.
  const [tzManuallyPickedThisSession, setTzManuallyPickedThisSession] = useState(false);

  // External links state
  const [editLinks, setEditLinks] = useState<ExternalLink[]>([]);

  // Track if form has unsaved changes
  const [detailsChanged, setDetailsChanged] = useState(false);
  const [linksChanged, setLinksChanged] = useState(false);

  // Track recently saved links for visual feedback (#1108)
  const [recentlySavedLinks, setRecentlySavedLinks] = useState<Set<number>>(new Set());
  const [savingLinkIndex, setSavingLinkIndex] = useState<number | null>(null);

  // Avatar upload state
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Success/error messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showAvatarRemoveConfirm, setShowAvatarRemoveConfirm] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // #3346: the TopBar club switcher (rendered here because the URL carries
  // :slug — TopBar.tsx) navigates IN PLACE between /clubs/:slug/settings/general
  // routes, so React reuses this component instance and only `slug` changes.
  // Reset the per-section dirty flags on a slug change so the destination
  // club's data rehydrates via the init effect below. Without this, a dirty
  // section would retain the PREVIOUS club's draft while useUpdateClub(slug)
  // rebinds to the new slug — Save would then write one club's values onto
  // another. (Native stack-pushes a fresh screen per slug, so it has no
  // equivalent; this guard is specific to web's in-place switch.)
  useEffect(() => {
    setDetailsChanged(false);
    setLinksChanged(false);
  }, [slug]);

  // Initialize form data when club loads.
  // #3346: gate each section's rehydration on its own dirty flag, mirroring
  // native (mobile/app/(protected)/clubs/[slug]/settings/general.tsx). Saving
  // one section invalidates ['club', slug] (useClubs.ts:154-157), which
  // refetches and re-runs this effect; without the guard the refetch wipes
  // unsaved edits in the OTHER section. Each section only re-hydrates while
  // pristine; a dirty section keeps the user's typing until ITS OWN save
  // resets the flag (handleSaveDetails / handleSaveLinks below).
  const externalLinks = clubData?.external_links;
  useEffect(() => {
    if (club && !detailsChanged) {
      setEditName(club.name);
      setEditDescription(club.description || '');
      setEditMeetingMode(club.meeting_mode || 'in_person');
      setEditCity(club.city || '');
      setEditState(club.state || '');
      setEditRegion(club.region || null);
      // #1656: load the persisted tz so the admin sees the actual current
      // value (including a wrong one — that's the owner-travel signal).
      // We deliberately do NOT set tzManuallyPickedThisSession here;
      // see the state declaration for why.
      if (club.timezone) {
        setEditTimezone(club.timezone);
      }
    }
    if (externalLinks && !linksChanged) {
      setEditLinks(externalLinks);
    }
  }, [club, externalLinks, detailsChanged, linksChanged]);

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // External links helpers
  const usedTypes = editLinks.map(l => l.type);
  const availableTypes = LINK_TYPES.filter(t =>
    t.type === 'custom'
      ? !editLinks.some(l => l.type === 'custom')
      : !usedTypes.includes(t.type)
  );

  const addLink = (type: ExternalLinkType) => {
    if (editLinks.length >= 5) return;
    setEditLinks([...editLinks, { type, url: '', label: type === 'custom' ? '' : undefined }]);
  };

  const updateLink = (index: number, field: 'url' | 'label', value: string) => {
    const newLinks = [...editLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setEditLinks(newLinks);
  };

  const removeLink = (index: number) => {
    setEditLinks(editLinks.filter((_, i) => i !== index));
  };

  // Handlers
  const handleSaveDetails = async () => {
    if (!club) return;

    // #1363: Include meeting_mode and conditional location/region
    const hasLocation = editMeetingMode === 'in_person' || editMeetingMode === 'hybrid';
    const hasRegion = editMeetingMode === 'virtual' || editMeetingMode === 'hybrid';

    try {
      await updateClubMutation.mutateAsync({
        name: editName.trim(),
        // #2960: emptied fields map to `null` (clears the column), not `undefined`
        // (dropped by JSON.stringify → column never cleared). The inactive-mode
        // branch stays `undefined` (untouched) — web does NOT clear the other
        // mode's fields on a switch (the #2158 web/native divergence).
        description: emptyToNull(editDescription),
        meeting_mode: editMeetingMode,
        city: hasLocation ? emptyToNull(editCity) : undefined,
        state: hasLocation ? emptyToNull(editState) : undefined,
        region: hasRegion ? editRegion : undefined,
        // #1656: club tz drives in-person notification rendering
        timezone: editTimezone,
      });
      // #3346: useUpdateClub invalidates ['club', slug] but does NOT await the
      // refetch (useOroMutation drops the onSuccess return; useClubs.ts:155),
      // so mutateAsync resolves while the cache is still pre-save. Await the
      // refetch here BEFORE releasing the dirty guard — otherwise clearing
      // detailsChanged would let the init effect rehydrate this section from
      // the STALE cache, and an edit during the refetch could be saved back
      // over the values just persisted (Frank + cloud Codex P1). Idempotent:
      // it awaits the in-flight refetch the mutation already triggered.
      await queryClient.invalidateQueries({ queryKey: ['club', slug || ''] });
      setSuccessMessage('Club details updated successfully');
      setDetailsChanged(false);
    } catch {
      // Error toast handled by useOroMutation
    }
  };

  const handleSaveLinks = async () => {
    if (!club) return;
    // Normalize URLs: prepend https:// if no protocol specified
    const validLinks = editLinks
      .filter(l => l.url.trim())
      .map(l => ({
        ...l,
        url: /^https?:\/\//i.test(l.url) ? l.url : `https://${l.url}`,
      }));

    try {
      await updateClubMutation.mutateAsync({ external_links: validLinks });
      // #3346: await the refetch before releasing the guard — see handleSaveDetails.
      await queryClient.invalidateQueries({ queryKey: ['club', slug || ''] });
      setSuccessMessage('External links updated successfully');
      setLinksChanged(false);
    } catch {
      // Error toast handled by useOroMutation
    }
  };

  // Save a single link with visual feedback (#1108)
  const handleSaveSingleLink = async (index: number) => {
    if (!club || !editLinks[index]?.url.trim()) return;

    setSavingLinkIndex(index);

    // Normalize URLs: prepend https:// if no protocol specified
    const validLinks = editLinks
      .filter(l => l.url.trim())
      .map(l => ({
        ...l,
        url: /^https?:\/\//i.test(l.url) ? l.url : `https://${l.url}`,
      }));

    try {
      await updateClubMutation.mutateAsync({ external_links: validLinks });
      // #3346: await the refetch before releasing the guard — see handleSaveDetails.
      await queryClient.invalidateQueries({ queryKey: ['club', slug || ''] });
      setLinksChanged(false);

      // Mark this link as recently saved
      setRecentlySavedLinks(prev => new Set(prev).add(index));

      // Clear the "saved" indicator after 2 seconds
      setTimeout(() => {
        setRecentlySavedLinks(prev => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }, 2000);
    } catch {
      // Error toast handled by useOroMutation
    } finally {
      setSavingLinkIndex(null);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    if (!club) return;

    setIsAvatarUploading(true);
    setAvatarError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await guardedFetch(`${API_BASE_URL}/upload/club-avatar/${club.id}`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      setSuccessMessage('Club avatar updated successfully');
      window.location.reload();
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to upload avatar');
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleAvatarRemoveClick = () => {
    setShowAvatarRemoveConfirm(true);
  };

  const handleAvatarRemoveConfirm = async () => {
    if (!club) return;
    setShowAvatarRemoveConfirm(false);

    setIsAvatarUploading(true);
    setAvatarError(null);

    try {
      const response = await guardedFetch(`${API_BASE_URL}/upload/club-avatar/${club.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Removal failed');
      }

      setSuccessMessage('Club avatar removed successfully');
      window.location.reload();
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to remove avatar');
    } finally {
      setIsAvatarUploading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Club Details" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4 space-y-6">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error/permission state
  if (error || !isOwner || !club) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Club Details" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">
              {error ? 'Unable to load settings' : 'Only club owners can access settings'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Picker may have a stored value not in COMMON_TIMEZONES (e.g. America/Detroit
  // captured from browser on club create). Mirror ProfileSettingsPage so the
  // option is renderable and the current value displays correctly. (#1665)
  const optionsWithCurrent = COMMON_TIMEZONES.some(t => t.value === editTimezone)
    ? COMMON_TIMEZONES
    : [{ value: editTimezone, label: editTimezone }, ...COMMON_TIMEZONES];

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Club Details" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="rounded-lg border border-oro-success/40 bg-oro-success/10 p-4">
              <p className="text-sm text-oro-success">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {updateClubMutation.error && (
            <div className="rounded-lg border border-oro-error/40 bg-oro-error/10 p-4">
              <p className="text-sm text-oro-error">
                {(updateClubMutation.error as Error).message || 'An error occurred'}
              </p>
            </div>
          )}

          {/* Club Details Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">Club Details</h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-4">
              {/* Club Name */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Club Name
                </label>
                <Input
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setDetailsChanged(true);
                  }}
                  placeholder="Club name"
                  maxLength={100}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => {
                    setEditDescription(e.target.value);
                    setDetailsChanged(true);
                  }}
                  placeholder="Tell people about your club..."
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface-alt text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-50 resize-none transition-colors"
                />
              </div>

              {/* Meeting Mode (#1363) */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-secondary">
                  How does your club meet?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditMeetingMode('in_person');
                      setDetailsChanged(true);
                    }}
                    className={`p-3 border rounded-lg text-left transition-all ${
                      editMeetingMode === 'in_person'
                        ? 'border-accent bg-accent/5'
                        : 'border-oro-border hover:border-oro-border-hover'
                    }`}
                  >
                    <MapPin className="w-4 h-4 text-text-secondary mb-1" />
                    <div className="font-medium text-text-primary text-sm">In Person</div>
                    <p className="text-xs text-text-muted mt-0.5">Local meetups</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditMeetingMode('virtual');
                      setDetailsChanged(true);
                    }}
                    className={`p-3 border rounded-lg text-left transition-all ${
                      editMeetingMode === 'virtual'
                        ? 'border-accent bg-accent/5'
                        : 'border-oro-border hover:border-oro-border-hover'
                    }`}
                  >
                    <Globe className="w-4 h-4 text-text-secondary mb-1" />
                    <div className="font-medium text-text-primary text-sm">Virtual</div>
                    <p className="text-xs text-text-muted mt-0.5">Online only</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditMeetingMode('hybrid');
                      setDetailsChanged(true);
                    }}
                    className={`p-3 border rounded-lg text-left transition-all ${
                      editMeetingMode === 'hybrid'
                        ? 'border-accent bg-accent/5'
                        : 'border-oro-border hover:border-oro-border-hover'
                    }`}
                  >
                    <Zap className="w-4 h-4 text-text-secondary mb-1" />
                    <div className="font-medium text-text-primary text-sm">Hybrid</div>
                    <p className="text-xs text-text-muted mt-0.5">Both options</p>
                  </button>
                </div>
              </div>

              {/* Location - for in_person and hybrid clubs */}
              {(editMeetingMode === 'in_person' || editMeetingMode === 'hybrid') && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-text-secondary">
                    <MapPin className="w-4 h-4" />
                    <label className="block text-sm font-medium">Location</label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={editCity}
                      onChange={(e) => {
                        setEditCity(e.target.value);
                        setDetailsChanged(true);
                      }}
                      placeholder="City"
                    />
                    <select
                      value={editState}
                      onChange={(e) => {
                        const next = e.target.value;
                        setEditState(next);
                        setDetailsChanged(true);
                        // #1656: when state changes and admin hasn't manually
                        // touched the tz field this session, derive a sensible
                        // default. Re-firing for an existing club is intentional
                        // — it's the owner-travel fix (Frank P1 on #1657).
                        if (!tzManuallyPickedThisSession && next && next in STATE_TO_TIMEZONE) {
                          setEditTimezone(STATE_TO_TIMEZONE[next as USState]);
                        }
                      }}
                      className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface-alt text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                    >
                      <option value="">State</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Timezone — #1656. Drives notification rendering for in-person
                  events. Auto-defaults from state for in_person/hybrid clubs;
                  always editable so admin can override (multi-tz states, etc.) */}
              {(editMeetingMode === 'in_person' || editMeetingMode === 'hybrid') && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-text-secondary">
                    <Clock className="w-4 h-4" />
                    <label className="block text-sm font-medium">
                      Timezone <span className="text-text-muted font-normal">({timezoneAbbreviation(editTimezone)})</span>
                    </label>
                  </div>
                  <select
                    value={editTimezone}
                    onChange={(e) => {
                      setEditTimezone(e.target.value);
                      // Manual pick locks in the choice — subsequent state
                      // changes won't auto-rewrite it.
                      setTzManuallyPickedThisSession(true);
                      setDetailsChanged(true);
                    }}
                    className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface-alt text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                  >
                    {optionsWithCurrent.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-text-muted">
                    Used for event time rendering in notifications and shared links.
                    {editState && !tzManuallyPickedThisSession && ' Defaulted from your state — change if your members meet in a different zone.'}
                  </p>
                </div>
              )}

              {/* Region - for virtual and hybrid clubs */}
              {(editMeetingMode === 'virtual' || editMeetingMode === 'hybrid') && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-text-secondary">
                    <Globe className="w-4 h-4" />
                    <label className="block text-sm font-medium">
                      Primary Region <span className="text-text-muted font-normal">(optional)</span>
                    </label>
                  </div>
                  <select
                    value={editRegion || ''}
                    onChange={(e) => {
                      setEditRegion((e.target.value || null) as ClubRegion | null);
                      setDetailsChanged(true);
                    }}
                    className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface-alt text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                  >
                    <option value="">Global (no specific region)</option>
                    <option value="americas">Americas</option>
                    <option value="emea">Europe, Middle East & Africa</option>
                    <option value="apac">Asia Pacific</option>
                  </select>
                  <p className="text-xs text-text-muted">
                    Helps members find clubs active in their timezone
                  </p>
                </div>
              )}

              {/* Save Button - only show when changed */}
              {detailsChanged && (
                <Button
                  variant="primary"
                  onClick={handleSaveDetails}
                  loading={updateClubMutation.isPending}
                  disabled={!editName.trim()}
                  className="w-full"
                >
                  Save Changes
                </Button>
              )}
            </div>
          </section>

          {/* Club Avatar Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">Avatar</h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
              <p className="text-sm text-text-secondary mb-4">
                Round profile image shown in club lists. Falls back to cover photo if not set. Recommended size: 200×200px.
              </p>

              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-oro-surface-alt flex items-center justify-center text-3xl font-bold text-text-primary ring-2 ring-oro-border overflow-hidden">
                    {club.avatar_image_url || club.hero_image_url ? (
                      <img
                        src={(club.avatar_image_url || club.hero_image_url) as string}
                        alt={club.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      club.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const input = document.getElementById('club-avatar-input') as HTMLInputElement;
                      input?.click();
                    }}
                    disabled={isAvatarUploading}
                    className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-oro-bg border border-oro-surface flex items-center justify-center transition-colors ${
                      isAvatarUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-oro-surface-alt'
                    }`}
                  >
                    <Camera className={`w-4 h-4 text-text-primary ${isAvatarUploading ? 'animate-pulse' : ''}`} />
                  </button>
                  <input
                    id="club-avatar-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleAvatarUpload(file);
                      }
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                </div>

                {club.avatar_image_url && (
                  <button
                    onClick={handleAvatarRemoveClick}
                    disabled={isAvatarUploading}
                    className="text-sm text-text-secondary hover:text-oro-error transition-colors"
                  >
                    Remove Avatar
                  </button>
                )}
              </div>

              {avatarError && (
                <div className="mt-2 p-3 bg-oro-error/10 border border-oro-error/20 rounded-lg text-sm text-oro-error">
                  {avatarError}
                </div>
              )}
            </div>
          </section>

          {/* External Links Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">External Links</h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
              <p className="text-sm text-text-secondary mb-4">
                Add links to your club's website, Instagram, Facebook, or YouTube. Max 5 links.
              </p>

              <div className="space-y-4">
                {editLinks.map((link, index) => {
                  const linkType = LINK_TYPES.find(t => t.type === link.type);
                  const isSaving = savingLinkIndex === index;
                  const isSaved = recentlySavedLinks.has(index);
                  const hasValidUrl = link.url.trim().length > 0;

                  return (
                    <div key={index} className="flex items-start gap-3 p-3 bg-oro-surface-alt rounded-lg">
                      <div className="flex items-center justify-center w-8 h-8 shrink-0">
                        {linkType?.icon}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-text-primary">{linkType?.label}</span>
                          <div className="flex items-center gap-2">
                            {/* Saved indicator (#1108) */}
                            {isSaved && (
                              <span className="text-xs text-oro-success font-medium animate-fade-in">
                                Saved
                              </span>
                            )}
                            {/* Inline save button (#1108) */}
                            {hasValidUrl && !isSaved && (
                              <button
                                onClick={() => handleSaveSingleLink(index)}
                                disabled={isSaving}
                                className="p-1 rounded text-text-muted hover:text-oro-success hover:bg-oro-success/10 transition-colors disabled:opacity-50"
                                title="Save this link"
                              >
                                {isSaving ? (
                                  <OroLoader variant="bounce" size={16} label="Saving" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            {/* Remove button */}
                            <button
                              onClick={() => {
                                removeLink(index);
                                setLinksChanged(true);
                              }}
                              className="p-1 rounded text-text-muted hover:text-oro-error hover:bg-oro-error/10 transition-colors"
                              title="Remove this link"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <Input
                          type="url"
                          value={link.url}
                          onChange={(e) => {
                            updateLink(index, 'url', e.target.value);
                            setLinksChanged(true);
                          }}
                          placeholder={linkType?.placeholder}
                        />
                        {link.type === 'custom' && (
                          <Input
                            value={link.label || ''}
                            onChange={(e) => {
                              updateLink(index, 'label', e.target.value);
                              setLinksChanged(true);
                            }}
                            placeholder="Link label (e.g., 'Our Newsletter')"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                {editLinks.length < 5 && availableTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {availableTypes.map(linkType => (
                      <button
                        key={linkType.type}
                        onClick={() => {
                          addLink(linkType.type);
                          setLinksChanged(true);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-oro-surface-alt border border-oro-border rounded-lg text-sm text-text-secondary hover:text-text-primary hover:border-accent/50 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        {linkType.icon}
                        <span>{linkType.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Save Button - only show when changed */}
                {linksChanged && (
                  <Button
                    variant="primary"
                    onClick={handleSaveLinks}
                    loading={updateClubMutation.isPending}
                    className="w-full"
                  >
                    Save Links
                  </Button>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Avatar Remove Confirmation */}
      <ConfirmDialog
        isOpen={showAvatarRemoveConfirm}
        title="Remove Club Avatar"
        message="Remove club avatar? It will fall back to the cover photo."
        confirmLabel="Remove"
        cancelLabel="Keep"
        variant="destructive"
        isPending={isAvatarUploading}
        onConfirm={handleAvatarRemoveConfirm}
        onCancel={() => setShowAvatarRemoveConfirm(false)}
      />
    </div>
  );
}
